export type * from '@hirebridge/types'

import type {
  Application,
  ApiError,
  AppNotification,
  AttentionItem,
  ChannelPerformanceRow,
  ChatMessage,
  ShareLinkSet,
  CheckoutSession,
  ConversationSummary,
  ChatContact,
  CvDetail,
  CvSummary,
  ContractSummary,
  CvTemplateSummary,
  MessagePage,
  MilestoneSummary,
  ProjectSummary,
  ProposalSummary,
  Invoice,
  PaymentConfirmation,
  PricingPlan,
  CompanyRole,
  Company,
  SalaryInsight,
  ContentArticle,
  CustomFieldSchema,
  EmployerDashboard,
  Entitlements,
  FunnelRow,
  Interview,
  Job,
  JobPerformance,
  JobSearchParams,
  Offer,
  Paginated,
  Pipeline,
  TeamMember,
  TimeToHire,
  User,
} from '@hirebridge/types'



/**
 * The single HTTP client, shared by web and mobile.
 *
 * Auth differs by platform and is deliberately abstracted behind `getToken`:
 * the web app authenticates by session cookie (so it returns null and relies
 * on `credentials: include`), while the mobile app supplies a bearer token
 * from secure storage. No call site needs to know which.
 */

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string[]>,
    public readonly upgradeUrl?: string,
    public readonly feature?: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }

  /** A 402 means the action is real but the plan does not allow it yet. */
  get isPaywall(): boolean {
    return this.status === 402
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isValidation(): boolean {
    return this.status === 422
  }
}

export interface ApiClientOptions {
  baseUrl: string
  /** Return a bearer token (mobile) or null to use cookies (web). */
  getToken?: () => string | null | Promise<string | null>
  /** Company context for employer endpoints. */
  getCompanyId?: () => number | null
  onUnauthenticated?: () => void
}

function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match && match[1] ? decodeURIComponent(match[1]) : null
}

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  /**
   * Exposed for the few places that need a URL rather than a fetch — notably
   * the CV preview iframe, which must load the server-rendered document
   * directly instead of receiving it as a string.
   */
  get baseUrl(): string {
    return this.options.baseUrl
  }

  private async headers(hasBody: boolean, isFormData = false): Promise<HeadersInit> {
    const headers: Record<string, string> = { Accept: 'application/json' }

    if (hasBody && !isFormData) headers['Content-Type'] = 'application/json'

    const token = await this.options.getToken?.()
    if (token) headers.Authorization = `Bearer ${token}`

    const companyId = this.options.getCompanyId?.()
    if (companyId) headers['X-Company-Id'] = String(companyId)

    const csrfToken = getCsrfToken()
    if (csrfToken) headers['X-XSRF-TOKEN'] = csrfToken

    return headers
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const hasBody = init.body !== undefined
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData

    let response: Response
    try {
      response = await fetch(`${this.options.baseUrl}${path}`, {
        ...init,
        headers: { ...(await this.headers(hasBody, isFormData)), ...(init.headers ?? {}) },
        // Required for the web app's cookie session; harmless for mobile.
        credentials: 'include',
      })
    } catch (netErr) {
      throw new ApiClientError(
        0,
        'NetworkError',
        'Cannot connect to backend server. Please check your network or ensure the backend server is running.',
      )
    }

    if (response.status === 204) return undefined as T

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      const error = (payload as ApiError | null)?.error

      if (response.status === 401) this.options.onUnauthenticated?.()

      throw new ApiClientError(
        response.status,
        error?.code ?? 'UnknownError',
        error?.message ?? 'Something went wrong. Please try again.',
        error?.fields,
        error?.upgrade_url,
        error?.feature,
      )
    }

    return payload as T
  }

  private query(params: Record<string, unknown>): string {
    const search = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue

      if (Array.isArray(value)) {
        value.forEach((v) => search.append(`${key}[]`, String(v)))
      } else {
        search.append(key, String(value))
      }
    }

    const s = search.toString()
    return s ? `?${s}` : ''
  }

  /* ------------------------------------------------------------------ auth */

  login = (email: string, password: string, deviceName?: string) =>
    this.request<{ user: User; token?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, device_name: deviceName }),
    })

  register = (input: Record<string, unknown>) =>
    this.request<{ user: User; token?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    })

  oauthGoogle = (payload: {
    credential?: string
    token?: string
    email?: string
    first_name?: string
    last_name?: string
    avatar_url?: string
    role?: 'candidate' | 'employer' | 'freelancer'
    company_name?: string
  }) =>
    this.request<{ user: User; token?: string }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify(payload),
    })

  googleAuthUrl = () =>
    this.request<{ url?: string; client_id?: string }>('/auth/google/url')

  logout = () => this.request<void>('/auth/logout', { method: 'POST' })

  me = () => this.request<{ user: User }>('/me')

  forgotPassword = (identifierOrEmail: string) =>
    this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email: identifierOrEmail, identifier: identifierOrEmail }),
    })

  resetPassword = (payload: { email?: string; identifier?: string; code: string; password: string; password_confirmation: string }) =>
    this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    })

  requestOtp = (identifier: string, purpose: 'login' | 'reset' | 'verify_phone' | 'verify_email' = 'login') =>
    this.request<{ message: string; channel: string }>('/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({ identifier, purpose }),
    })

  verifyOtp = (identifier: string, code: string, deviceName?: string) =>
    this.request<{ user: User; token?: string }>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ identifier, code, device_name: deviceName }),
    })

  createCompany = (data: {
    name: string
    website?: string
    description?: string
    industry_id?: number
    size?: string
    primary_location_id?: number
  }) =>
    this.request<{ data: Company }>('/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  myCompanies = () => this.request<{ data: Company[] }>('/my/companies')

  currentCompany = () => this.request<{ data: Company }>('/employer/company')

  updateCompany = (data: Partial<Company> | Record<string, unknown>) =>
    this.request<{ data: Company }>('/employer/company', {
      method: 'PUT',
      body: JSON.stringify(data),
    })

  uploadCompanyLogo = (file: File | FormData) => {
    let bodyData: FormData
    if (file instanceof FormData) {
      bodyData = file
    } else {
      bodyData = new FormData()
      bodyData.append('logo', file)
    }
    return this.request<{ data: Company; logo_url: string }>('/employer/company/logo', {
      method: 'POST',
      body: bodyData as unknown as BodyInit,
    })
  }

  /* ------------------------------------------------------------------ jobs */

  searchJobs = (params: JobSearchParams = {}) =>
    this.request<Paginated<Job>>(`/jobs${this.query(params as Record<string, unknown>)}`)

  jobFacets = (params: JobSearchParams = {}) =>
    this.request<{ data: Record<string, Record<string, number>> }>(
      `/jobs/facets${this.query(params as Record<string, unknown>)}`,
    )

  job = (slug: string) => this.request<{ data: Job }>(`/jobs/${slug}`)

  /* ------------------------------------------------------------- govt jobs */

  govtJobs = (params: import('@hirebridge/types').GovtJobSearchParams = {}) =>
    this.request<import('@hirebridge/types').Paginated<import('@hirebridge/types').GovtJob>>(
      `/govt-jobs${this.query(params as Record<string, unknown>)}`
    )

  govtJob = (slug: string) =>
    this.request<{
      data: import('@hirebridge/types').GovtJob
      related: import('@hirebridge/types').GovtJob[]
    }>(`/govt-jobs/${slug}`)

  govtJobFilters = () =>
    this.request<import('@hirebridge/types').GovtJobFilterOptions>('/govt-jobs/filters/options')


  /* ---------------------------------------------------------- applications */

  apply = (jobId: string, input: Record<string, unknown>) =>
    this.request<{ data: Application }>(`/candidate/jobs/${jobId}/apply`, {
      method: 'POST',
      body: JSON.stringify(input),
    })

  myApplications = (page = 1) =>
    this.request<Paginated<Application>>(`/candidate/applications?page=${page}`)

  application = (id: string) =>
    this.request<{ data: Application }>(`/candidate/applications/${id}`)

  withdrawApplication = (id: string) =>
    this.request<{ data: Application }>(`/candidate/applications/${id}/withdraw`, {
      method: 'POST',
    })

  /* ------------------------------------------------------------- candidate */

  candidateProfile = () => this.request<{ data: import('@hirebridge/types').CandidateProfileDetail }>('/candidate/profile')

  updateCandidateProfile = (input: Record<string, unknown>) =>
    this.request<{ data: import('@hirebridge/types').CandidateProfileDetail }>('/candidate/profile', {
      method: 'PUT',
      body: JSON.stringify(input),
    })

  uploadCandidateAvatar = (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return this.request<{ data: import('@hirebridge/types').CandidateProfileDetail }>('/candidate/profile/avatar', {
      method: 'POST',
      body: formData as unknown as BodyInit,
    })
  }

  candidateResumes = () =>
    this.request<{
      data: Array<{
        id: number
        label: string | null
        original_name: string
        size_bytes: number
        parse_status: string
        created_at: string
      }>
    }>('/candidate/resumes')

  uploadCandidateResume = (file: File, label?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (label) formData.append('label', label)
    return this.request<{ data: { id: number; parse_status: string } }>('/candidate/resumes', {
      method: 'POST',
      body: formData as unknown as BodyInit,
    })
  }

  deleteCandidateResume = (id: number) =>
    this.request<void>(`/candidate/resumes/${id}`, { method: 'DELETE' })

  savedJobs = () => this.request<Paginated<Job>>('/candidate/saved-jobs')

  saveJob = (jobId: string) =>
    this.request<void>(`/candidate/saved-jobs/${jobId}`, { method: 'POST' })

  unsaveJob = (jobId: string) =>
    this.request<void>(`/candidate/saved-jobs/${jobId}`, { method: 'DELETE' })

  /* -------------------------------------------------------------- employer */

  employerDashboard = () => this.request<{ data: EmployerDashboard }>('/employer/dashboard')

  employerJobs = (status?: string) =>
    this.request<Paginated<Job>>(`/employer/jobs${this.query({ status })}`)

  createJob = (input: Record<string, unknown>) =>
    this.request<{ data: Job }>('/employer/jobs', {
      method: 'POST',
      body: JSON.stringify(input),
    })

  publishJob = (id: string) =>
    this.request<{ data: Job }>(`/employer/jobs/${id}/publish`, { method: 'POST' })

  employerApplications = (params: Record<string, unknown> = {}) =>
    this.request<Paginated<Application>>(`/employer/applications${this.query(params)}`)

  moveApplication = (id: string, stageId: number, note?: string) =>
    this.request<{ data: Application }>(`/employer/applications/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ stage_id: stageId, note }),
    })

  entitlements = () => this.request<{ data: Entitlements }>('/employer/entitlements')

  bulkMoveApplications = (ids: string[], stageId: number, note?: string) =>
    this.request<{ data?: { updated: number }; moved?: number }>('/employer/applications/bulk-move', {
      method: 'POST',
      body: JSON.stringify({ application_ids: ids, stage_id: stageId, note }),
    })

  addApplicationNote = (id: string, body: string) =>
    this.request<{ data: { id: number; body: string; created_at: string } }>(
      `/employer/applications/${id}/notes`,
      { method: 'POST', body: JSON.stringify({ body }) },
    )

  /* ------------------------------------------------------------- pipelines */

  pipelines = () => this.request<{ data: Pipeline[] }>('/employer/pipelines')

  createPipeline = (input: Record<string, unknown>) =>
    this.request<{ data: Pipeline }>('/employer/pipelines', {
      method: 'POST',
      body: JSON.stringify(input),
    })

  /* ------------------------------------------------------------------ team */

  team = () =>
    this.request<{ data: TeamMember[]; meta: { seats_remaining: number | null } }>(
      '/employer/team',
    )

  inviteTeamMember = (email: string, role: CompanyRole) =>
    this.request<{ message: string }>('/employer/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })

  updateTeamRole = (memberId: string, role: CompanyRole) =>
    this.request<{ message: string }>(`/employer/team/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })

  removeTeamMember = (memberId: string) =>
    this.request<void>(`/employer/team/${memberId}`, { method: 'DELETE' })

  /* ------------------------------------------------------ interviews/offers */

  interviews = () => this.request<{ data: Interview[] }>('/employer/interviews')

  scheduleInterview = (applicationId: string, input: Record<string, unknown>) =>
    this.request<{ data: Interview }>(`/employer/applications/${applicationId}/interviews`, {
      method: 'POST',
      body: JSON.stringify(input),
    })

  submitInterviewFeedback = (interviewId: number, input: Record<string, unknown>) =>
    this.request<{ data: { id: number; recommendation: string } }>(
      `/employer/interviews/${interviewId}/feedback`,
      { method: 'POST', body: JSON.stringify(input) },
    )

  submitScorecard = (applicationId: string, input: Record<string, unknown>) =>
    this.request<{ data: any }>(
      `/employer/applications/${applicationId}/scorecard`,
      { method: 'POST', body: JSON.stringify(input) },
    )

  createOffer = (applicationId: string, input: Record<string, unknown>) =>
    this.request<{ data: Offer }>(`/employer/applications/${applicationId}/offers`, {
      method: 'POST',
      body: JSON.stringify(input),
    })

  approveOffer = (offerId: number) =>
    this.request<{ data: Offer }>(`/employer/offers/${offerId}/approve`, { method: 'POST' })

  sendOffer = (offerId: number) =>
    this.request<{ data: Offer }>(`/employer/offers/${offerId}/send`, { method: 'POST' })

  /* ------------------------------------------------------------- analytics */

  funnel = (jobId?: string) =>
    this.request<{ data: FunnelRow[] }>(`/employer/analytics/funnel${this.query({ job_id: jobId })}`)

  timeToHire = () => this.request<{ data: TimeToHire }>('/employer/analytics/time-to-hire')

  jobPerformance = () =>
    this.request<{ data: JobPerformance[] }>('/employer/analytics/job-performance')

  applicationSources = () =>
    this.request<{ data: Record<string, number> }>('/employer/analytics/sources')

  attention = () => this.request<{ data: AttentionItem[] }>('/employer/analytics/attention')

  /* ------------------------------------------------------------ cv builder */

  cvTemplates = () => this.request<{ data: CvTemplateSummary[] }>('/cv-templates')

  cvs = () => this.request<{ data: CvSummary[] }>('/candidate/cvs')

  cv = (id: string) => this.request<{ data: CvDetail }>(`/candidate/cvs/${id}`)

  createCv = (input: Record<string, unknown>) =>
    this.request<{ data: { id: string } }>('/candidate/cvs', {
      method: 'POST',
      body: JSON.stringify(input),
    })

  updateCv = (id: string, input: Record<string, unknown>) =>
    this.request<{ data: CvDetail }>(`/candidate/cvs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })

  deleteCv = (id: string) => this.request<void>(`/candidate/cvs/${id}`, { method: 'DELETE' })

  /** Queued — returns 202 with a render id, or a URL if already cached. */
  renderCv = (id: string) =>
    this.request<{ data: { status: string; download_url?: string; render_id?: number } }>(
      `/candidate/cvs/${id}/render`,
      { method: 'POST' },
    )

  /* -------------------------------------------------------------- messaging */

  conversations = () =>
    this.request<{ data: ConversationSummary[]; meta: { total: number; unread: number } }>(
      '/conversations',
    )

  messages = (conversationId: string, since?: number) =>
    this.request<MessagePage>(
      `/conversations/${conversationId}/messages${this.query({ since })}`,
    )

  sendMessage = (conversationId: string, body: string) =>
    this.request<{ data: ChatMessage }>(`/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    })

  markConversationRead = (conversationId: string) =>
    this.request<void>(`/conversations/${conversationId}/read`, {
      method: 'POST',
    })

  startDirectConversation = (params: {
    recipient_id: number
    subject?: string
    message?: string
  }) =>
    this.request<{ data: { id: string; subject: string; recipient_name: string } }>(
      '/conversations/direct',
      {
        method: 'POST',
        body: JSON.stringify(params),
      },
    )

  searchChatContacts = (q?: string, role?: string) =>
    this.request<{ data: ChatContact[] }>(
      `/conversations/contacts${this.query({ q, role })}`,
    )

  unreadMessagesCount = () =>
    this.request<{ data: { unread_count: number } }>('/conversations/unread-count')

  openConversationForApplication = (applicationId: string) =>
    this.request<{ data: { id: string } }>(
      `/employer/applications/${applicationId}/conversation`,
      { method: 'POST' },
    )

  /* ---------------------------------------------------------- notifications */

  notifications = () =>
    this.request<{
      data: AppNotification[]
      meta: { unread: number; unread_messages: number }
    }>('/notifications')

  markNotificationRead = (id: string) =>
    this.request<void>(`/notifications/${id}/read`, { method: 'POST' })

  markAllNotificationsRead = () =>
    this.request<void>('/notifications/read-all', { method: 'POST' })

  /* --------------------------------------------------------------- billing */

  plans = (audience = 'employer') =>
    this.request<{ data: PricingPlan[] }>(`/billing/plans${this.query({ audience })}`)

  creditPacks = () => this.request<{ data: PricingPlan[] }>('/billing/credit-packs')

  currentBilling = () => this.request<{ data: Entitlements }>('/employer/billing/current')

  subscribe = (planKey: string) =>
    this.request<{ data: CheckoutSession }>('/employer/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan_key: planKey }),
    })

  cancelSubscription = () =>
    this.request<{ data: { status: string; access_until: string | null } }>(
      '/employer/billing/cancel',
      { method: 'POST' },
    )

  buyCredits = (packKey: string) =>
    this.request<{ data: CheckoutSession }>('/employer/billing/credits', {
      method: 'POST',
      body: JSON.stringify({ pack_key: packKey }),
    })

  /**
   * Confirms a completed checkout. The server verifies the signature; a valid
   * one only unlocks the UI, since the webhook is what actually activates the
   * plan.
   */
  verifyPayment = (confirmation: PaymentConfirmation) =>
    this.request<{ data: { verified: boolean; message: string } }>(
      '/employer/billing/verify',
      { method: 'POST', body: JSON.stringify(confirmation) },
    )

  /**
   * Razorpay Standard Order Creation
   */
  createOrder = (payload: { amount: number; currency?: string; receipt?: string; notes?: Record<string, unknown> }) =>
    this.request<{ success: boolean; order_id: string; amount: number; currency: string; key_id: string; receipt?: string }>(
      '/create-order',
      { method: 'POST', body: JSON.stringify(payload) },
    )

  /**
   * Razorpay Payment Signature Verification
   */
  verifyRazorpaySignature = (payload: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) =>
    this.request<{ success: boolean; message: string; data?: { order_id: string; payment_id: string } }>(
      '/verify-payment',
      { method: 'POST', body: JSON.stringify(payload) },
    )

  invoices = () =>
    this.request<{ data: Invoice[]; meta: { total: number } }>('/employer/billing/invoices')

  /* --------------------------------------------------------------- sharing */

  jobShareLinks = (slug: string) =>
    this.request<{ data: ShareLinkSet }>(`/jobs/${slug}/share`)

  companyShareLinks = (slug: string) =>
    this.request<{ data: ShareLinkSet }>(`/companies/${slug}/share`)

  /**
   * Records a click that arrived through a shared link. Called on load when
   * a `ref` query parameter is present.
   */
  registerShareClick = (token: string) =>
    this.request<{ data: { tracked: boolean } }>('/share/click', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })

  channelPerformance = () =>
    this.request<{ data: ChannelPerformanceRow[] }>('/employer/analytics/channels')

  /* ------------------------------------------------------------- freelance */

  projects = (params: Record<string, unknown> = {}) =>
    this.request<{ data: ProjectSummary[]; meta: { total: number } }>(
      `/projects${this.query(params)}`,
    )

  project = (id: string) => this.request<{ data: ProjectSummary }>(`/projects/${id}`)

  createProject = (input: Record<string, unknown>) =>
    this.request<{ data: ProjectSummary }>('/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    })

  myProjects = () => this.request<{ data: ProjectSummary[] }>('/my/projects')

  projectProposals = (projectId: string) =>
    this.request<{ data: ProposalSummary[] }>(`/projects/${projectId}/proposals`)

  submitProposal = (projectId: string, input: Record<string, unknown>) =>
    this.request<{ data: ProposalSummary }>(`/projects/${projectId}/proposals`, {
      method: 'POST',
      body: JSON.stringify(input),
    })

  myProposals = () => this.request<{ data: ProposalSummary[] }>('/my/proposals')

  acceptProposal = (proposalId: number) =>
    this.request<{ data: { contract_id: string; status: string } }>(
      `/proposals/${proposalId}/accept`,
      { method: 'POST' },
    )

  contracts = () => this.request<{ data: ContractSummary[] }>('/contracts')

  contract = (id: string) => this.request<{ data: ContractSummary }>(`/contracts/${id}`)

  fundMilestone = (id: number) =>
    this.request<{ data: MilestoneSummary }>(`/milestones/${id}/fund`, { method: 'POST' })

  submitMilestone = (id: number) =>
    this.request<{ data: MilestoneSummary }>(`/milestones/${id}/submit`, { method: 'POST' })

  approveMilestone = (id: number) =>
    this.request<{ data: MilestoneSummary }>(`/milestones/${id}/approve`, { method: 'POST' })

  addDeliverable = (milestoneId: number, input: Record<string, unknown>) =>
    this.request<{ data: { id: number } }>(`/milestones/${milestoneId}/deliverables`, {
      method: 'POST',
      body: JSON.stringify(input),
    })

  reviewContract = (contractId: string, input: Record<string, unknown>) =>
    this.request<{ data: { id: number; is_published: boolean; note: string | null } }>(
      `/contracts/${contractId}/review`,
      { method: 'POST', body: JSON.stringify(input) },
    )

  /* ---------------------------------------------------------------- browse */

  companies = (params: Record<string, unknown> = {}) =>
    this.request<Paginated<Company>>(`/companies${this.query(params)}`)

  company = (slug: string) => this.request<{ data: Company }>(`/companies/${slug}`)

  salaryInsights = (params: Record<string, unknown> = {}) =>
    this.request<{ data: SalaryInsight[] }>(`/salaries/insights${this.query(params)}`)

  contentPages = (params: Record<string, unknown> = {}) =>
    this.request<{ data: ContentArticle[] }>(`/content-pages${this.query(params)}`)

  contentPage = (slug: string) =>
    this.request<{ data: ContentArticle }>(`/content-pages/${slug}`)

  /* ---------------------------------------------------------------- offers */

  respondToOffer = (offerId: number, response: 'accepted' | 'declined', reason?: string) =>
    this.request<{ data: Offer }>(`/candidate/offers/${offerId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ response, reason }),
    })

  /* --------------------------------------------------- company verification */

  submitCompanyVerification = (data: { document_type: string; file: File; notes?: string } | FormData) => {
    let bodyData: FormData
    if (data instanceof FormData) {
      bodyData = data
    } else {
      bodyData = new FormData()
      bodyData.append('document_type', data.document_type)
      bodyData.append('file', data.file)
      if (data.notes) bodyData.append('notes', data.notes)
    }

    return this.request<{ data: { id: number; status: string; message: string } }>(
      '/employer/company/verification',
      {
        method: 'POST',
        body: bodyData as unknown as BodyInit,
      },
    )
  }

  submitJobForReview = (jobId: string) =>
    this.request<{ data: Job }>(`/employer/jobs/${jobId}/submit-review`, {
      method: 'POST',
    })

  /* --------------------------------------------------- talent crm & search */

  searchCandidates = (params: Record<string, unknown> = {}) =>
    this.request<{ data: import('@hirebridge/types').CandidateSearchResult[]; meta: { total: number; current_page: number } }>(
      `/employer/candidates/search${this.query(params)}`,
    )

  talentPools = () =>
    this.request<{ data: import('@hirebridge/types').TalentPool[] }>('/employer/talent-pools')

  createTalentPool = (data: { name: string; description?: string }) =>
    this.request<{ data: import('@hirebridge/types').TalentPool }>('/employer/talent-pools', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  deleteTalentPool = (id: number) =>
    this.request<void>(`/employer/talent-pools/${id}`, { method: 'DELETE' })

  addCandidateToPool = (poolId: number, candidateProfileId: number) =>
    this.request<{ data: { message: string } }>(`/employer/talent-pools/${poolId}/candidates`, {
      method: 'POST',
      body: JSON.stringify({ candidate_profile_id: candidateProfileId }),
    })

  removeCandidateFromPool = (poolId: number, candidateProfileId: number) =>
    this.request<void>(`/employer/talent-pools/${poolId}/candidates/${candidateProfileId}`, {
      method: 'DELETE',
    })

  candidateNotes = (candidateProfileId: number) =>
    this.request<{ data: import('@hirebridge/types').CandidateNote[] }>(
      `/employer/candidates/${candidateProfileId}/notes`,
    )

  addCandidateNote = (candidateProfileId: number, note: string, mentions?: string[]) =>
    this.request<{ data: import('@hirebridge/types').CandidateNote }>(
      `/employer/candidates/${candidateProfileId}/notes`,
      {
        method: 'POST',
        body: JSON.stringify({ note, mentions }),
      },
    )

  candidateTags = (candidateProfileId: number) =>
    this.request<{ data: string[] }>(`/employer/candidates/${candidateProfileId}/tags`)

  addCandidateTag = (candidateProfileId: number, tag: string) =>
    this.request<{ data: { tag: string } }>(`/employer/candidates/${candidateProfileId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tag }),
    })

  removeCandidateTag = (candidateProfileId: number, tag: string) =>
    this.request<void>(`/employer/candidates/${candidateProfileId}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    })

  inviteCandidate = (
    dataOrId: number | { candidate_profile_id: number; job_id: string | number; message?: string },
    jobId?: string | number,
    message?: string,
  ) => {
    const payload =
      typeof dataOrId === 'object'
        ? dataOrId
        : { candidate_profile_id: dataOrId, job_id: jobId, message }
    return this.request<{ data: { message: string } }>('/employer/candidates/invite', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  /* ------------------------------------------------ candidate privacy & alerts */

  candidatePrivacy = () =>
    this.request<{ data: import('@hirebridge/types').CandidatePrivacySettings }>('/candidate/privacy')

  updateCandidatePrivacy = (data: import('@hirebridge/types').CandidatePrivacySettings) =>
    this.request<{ data: import('@hirebridge/types').CandidatePrivacySettings }>('/candidate/privacy', {
      method: 'PUT',
      body: JSON.stringify(data),
    })

  jobAlerts = () =>
    this.request<{ data: import('@hirebridge/types').JobAlert[] }>('/candidate/job-alerts')

  createJobAlert = (data: Partial<import('@hirebridge/types').JobAlert>) =>
    this.request<{ data: import('@hirebridge/types').JobAlert }>('/candidate/job-alerts', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  deleteJobAlert = (id: number) =>
    this.request<void>(`/candidate/job-alerts/${id}`, { method: 'DELETE' })

  /* ---------------------------------------------------- bulk actions & ats */

  bulkAssignApplications = (data: import('@hirebridge/types').BulkAssignPayload) =>
    this.request<{ data: { updated: number } }>('/employer/applications/bulk/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  bulkRejectApplications = (data: import('@hirebridge/types').BulkRejectPayload) =>
    this.request<{ data: { updated: number } }>('/employer/applications/bulk/reject', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  bulkPoolCandidates = (
    poolIdOrData: number | { poolId: number; candidateProfileIds: number[] },
    candidateProfileIds?: number[],
  ) => {
    const payload =
      typeof poolIdOrData === 'object'
        ? { talent_pool_id: poolIdOrData.poolId, candidate_profile_ids: poolIdOrData.candidateProfileIds }
        : { talent_pool_id: poolIdOrData, candidate_profile_ids: candidateProfileIds }
    return this.request<{ data: { added: number } }>('/employer/talent-pools/bulk-add', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  closeJob = (jobId: string) =>
    this.request<{ data: Job }>(`/employer/jobs/${jobId}/close`, { method: 'POST' })

  /* --------------------------------------------- matching & recommendations */

  matchBreakdown = (jobId: string, candidateProfileId?: number) =>
    this.request<{ data: import('@hirebridge/types').MatchScoreBreakdown }>(
      `/jobs/${jobId}/match-breakdown${candidateProfileId ? `?candidate_id=${candidateProfileId}` : ''}`,
    )

  recommendedJobs = () =>
    this.request<{ data: Array<Job & { match_score: number; match_breakdown: import('@hirebridge/types').MatchScoreBreakdown }> }>(
      '/candidate/recommended-jobs',
    )

  recommendedCandidates = (jobId: string) =>
    this.request<{ data: import('@hirebridge/types').CandidateSearchResult[] }>(
      `/employer/jobs/${jobId}/recommended-candidates`,
    )

  sourceAttribution = (jobId?: string) =>
    this.request<{ data: import('@hirebridge/types').SourceAttributionRow[] }>(
      `/employer/analytics/sources${jobId ? `?job_id=${jobId}` : ''}`,
    )

  /* ------------------------------------------------ trust & safety reports */

  submitReport = (data: import('@hirebridge/types').ReportSubmission) =>
    this.request<{ data: { id: number; message: string } }>('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  /* --------------------------------- fibonaccipi fresher hub & assessments */

  getFresherHub = (params?: { type?: string; category_id?: number; page?: number }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString() : ''
    return this.request<import('@hirebridge/types').FresherHubResponse>(`/fresher-hub${qs}`)
  }

  getSkillAssessments = () =>
    this.request<{ data: import('@hirebridge/types').SkillAssessment[] }>('/assessments')

  getSkillAssessment = (slug: string) =>
    this.request<{ data: import('@hirebridge/types').SkillAssessment }>(`/assessments/${slug}`)

  submitSkillAssessment = (slug: string, answers: Record<number, number>) =>
    this.request<{ data: import('@hirebridge/types').CandidateAssessmentResult }>(`/candidate/assessments/${slug}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers }),
    })

  getReferralDashboard = () =>
    this.request<{ data: { stats: import('@hirebridge/types').ReferralStats; referrals: import('@hirebridge/types').ReferralItem[] } }>(
      '/candidate/referrals',
    )

  inviteReferral = (data: { email?: string; phone?: string; job_id?: number }) =>
    this.request<{ data: { id: number; message: string } }>('/candidate/referrals/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    })

  /* ---------------------------------------------------------------- config */

  config = () => this.request<{ data: Record<string, unknown> }>('/config')

  customFields = (entity: string) =>
    this.request<{ data: CustomFieldSchema[] }>(`/config/custom-fields/${entity}`)
}


export const createApiClient = (options: ApiClientOptions) => new ApiClient(options)